/**
 * FOCUS THAT THE USER DID NOT ASK FOR, ON A PHONE.
 *
 * Focusing a field on a touch screen opens the keyboard, and on iOS zooms the
 * page into a field whose text is under 16px — so a picker or a dialog that
 * focuses its search box the moment it opens jumps, zooms and covers itself.
 * A field is focused for the user only where there is a mouse and a keyboard;
 * on a touch screen it waits for the tap.
 */
export function canAutoFocusField(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/** A callback ref: focuses the field once, on mount — on a desktop only. */
export function autoFocusOnDesktop(el: HTMLElement | null) {
  if (el && canAutoFocusField()) el.focus({ preventScroll: true });
}

/**
 * iOS zooms into any focused field whose text is under 16px, and does not zoom
 * back. `maximum-scale=1` stops that auto-zoom; iOS still lets the user pinch
 * to zoom (it ignores the cap for gestures), so nothing is taken from them.
 * Applied on iOS only — elsewhere the cap WOULD block pinch-zoom.
 */
export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function capIosFocusZoom(doc: Document = document): void {
  const meta = doc.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!meta) return;
  const content = meta.getAttribute("content") ?? "";
  if (/maximum-scale/i.test(content)) return;
  meta.setAttribute("content", content ? `${content}, maximum-scale=1` : "width=device-width, initial-scale=1, maximum-scale=1");
}
